import { AdaptiveColor, Color, ColorName, getColor } from '@color';
import { LineSegment, lineSegment, point, Point, Rect } from '@geom';
import {
  EdgeDirection,
  Elements,
  topologicalSort,
  type EdgeEndpoint,
  type EID,
  type NID
} from '@graph';
import { LayoutConstants } from '@layout';
import { ENTITY_TOP, renderLayout, SnorfLayout } from '@snorf';
import { LocaleId, TextDirection } from '@strings';
import { textDirectionForParagraph } from '@typography';
import { clamp, expectDefined, lerpMap, produce } from '@utils';
import type { Visual } from '@visual';
import {
  ArrowType,
  drawArrow,
  drawArrowPath,
  drawLineSegment,
  drawMarker
} from '@visual';
import type { ConnectArrow } from '../visual/ConnectArrow';
import type { Doc } from './Doc';
import { GroupLayout } from './GroupLayout';
import type { NodeLayout } from './NodeLayout';
import { UnitLayout } from './UnitLayout';

export interface DrawDocDebugOpts {
  markerPoint: Point;
  markerRect: Rect;
}

export interface DrawDocInteractionOpts {
  selectBounds: Rect;
  connectArrow: ConnectArrow;
  nodeCreationIndicator1: Rect;
  nodeCreationIndicator2: Rect;
  highlightedEndpoint: EdgeEndpoint;
}

export interface DrawDocOpts {
  zoom: number;
  pan: Point;
  selection: Elements;
  hovered: Elements;
  copied: Elements;
  drawBackground: boolean;
  drawGrid: boolean;
  emphasizeGridAxes: boolean;
  interactionOpts?: Partial<DrawDocInteractionOpts>;
  debugOpts?: Partial<DrawDocDebugOpts>;
}

export const defaultDrawDocOpts: DrawDocOpts = {
  zoom: 1,
  pan: Point.ZERO,
  selection: Elements.make(),
  hovered: Elements.make(),
  copied: Elements.make(),
  drawBackground: true,
  drawGrid: true,
  emphasizeGridAxes: false
};

export function drawDoc(doc: Doc, visual: Visual, opts?: Partial<DrawDocOpts>) {
  new DocDrawer(doc, visual, opts).draw();
}

class DocDrawer {
  #doc: Doc;
  #visual: Visual;
  #opts: DrawDocOpts;

  constructor(doc: Doc, visual: Visual, opts?: Partial<DrawDocOpts>) {
    this.#doc = doc;
    this.#visual = visual;
    this.#opts = { ...defaultDrawDocOpts, ...opts };
  }

  get locale(): LocaleId {
    return this.#doc.locale;
  }

  get pan(): Point {
    return this.#opts.pan;
  }

  get zoom(): number {
    return this.#opts.zoom;
  }

  get selection(): Elements {
    return this.#opts.selection;
  }

  get hovered(): Elements {
    return this.#opts.hovered;
  }

  get copied(): Elements {
    // return this.#opts.selection;
    return this.#opts.copied;
  }

  get drawBackground(): boolean {
    return this.#opts.drawBackground;
  }

  get drawGrid(): boolean {
    return this.#opts.drawGrid;
  }

  get emphasizeGridAxes(): boolean {
    return this.#opts.emphasizeGridAxes;
  }

  // Interaction options

  get selectBounds(): Rect | undefined {
    return this.#opts.interactionOpts?.selectBounds;
  }

  get connectArrow(): ConnectArrow | undefined {
    return this.#opts.interactionOpts?.connectArrow;
  }

  get nodeCreationIndicator1(): Rect | undefined {
    return this.#opts.interactionOpts?.nodeCreationIndicator1;
  }

  get nodeCreationIndicator2(): Rect | undefined {
    return this.#opts.interactionOpts?.nodeCreationIndicator2;
  }

  get highlightedEndpoint(): EdgeEndpoint | undefined {
    return this.#opts.interactionOpts?.highlightedEndpoint;
  }

  // Debug options

  get markerPoint(): Point | undefined {
    return this.#opts.debugOpts?.markerPoint;
  }

  get markerRect(): Rect | undefined {
    return this.#opts.debugOpts?.markerRect;
  }

  isHovered(node: NID): boolean {
    return this.hovered.hasNode(node);
  }

  draw() {
    performance.mark('drawDoc:start');

    const visual = this.#visual;

    if (this.drawBackground) {
      visual.withGroup({ desc: 'Background' }, () => {
        visual.clear(this.getColor(ColorName.canvasBackground));
      });
    }

    visual.save();
    if (!this.pan.isZero) {
      visual.translate(this.pan);
    }
    if (this.zoom !== 1) {
      visual.scale(this.zoom);
    }

    const { bounds, scale } = visual.userSpaceBounds();

    if (this.drawGrid) {
      drawGrid(visual, bounds, scale, this.emphasizeGridAxes);
    }

    this.drawHalos();
    this.drawGroups();
    this.drawEdges();
    this.drawUnits();
    this.drawEndpointHighlights();
    this.drawNodeCreationIndicators();

    this.drawMarkerDot();
    this.drawMarkerRect();

    visual.restore();

    this.drawSelectBounds();
    this.drawConnectArrow();

    performance.mark('drawDoc:end');
    performance.measure('drawDoc', 'drawDoc:start', 'drawDoc:end');
  }

  private getColor(name: ColorName): Color {
    return getColor(name, this.#visual.colorMode);
  }

  private drawMarkerDot() {
    const markerPosition = this.markerPoint;
    if (markerPosition === undefined) {
      return;
    }
    drawMarker(this.#visual, markerPosition);
  }

  private drawMarkerRect() {
    const markerRect = this.markerRect;
    if (markerRect === undefined) {
      return;
    }
    this.#visual.drawRect(markerRect, {
      stroke: { color: Color.RED, width: 1 }
    });
  }

  private drawSelectBounds() {
    const bounds = this.selectBounds?.mul(this.zoom).add(this.pan);
    if (bounds === undefined) {
      return;
    }

    const width = 1;
    this.#visual.drawRect(bounds, {
      fill: this.getColor(ColorName.selectBoundsFill).withOpacity(0.1),
      stroke: { color: this.getColor(ColorName.selectBoundsStroke), width }
    });
  }

  private drawConnectArrow() {
    const { line, color, colorMode, headType, tailType } = this.connectArrow ?? {};

    if (line === undefined || color === undefined || colorMode === undefined) {
      return;
    }

    const positionedLine = line.mul(this.zoom).add(this.pan);

    drawArrow(this.#visual, positionedLine, color.realize(colorMode), 4, {
      headType,
      tailType
    });
  }

  private drawNodeCreationIndicators() {
    this.drawNodeCreationIndicator(this.nodeCreationIndicator1);
    this.drawNodeCreationIndicator(this.nodeCreationIndicator2);
  }

  private drawNodeCreationIndicator(bounds: Rect | undefined) {
    if (bounds === undefined) {
      return;
    }
    this.#visual.drawRect(bounds, {
      stroke: {
        color: this.getColor(ColorName.nodeCreationIndicatorStroke),
        width: LayoutConstants.NODE_CREATION_INDICATOR_STROKE_WIDTH
      },
      cornerRadius: LayoutConstants.NODE_CORNER_RADIUS
    });
  }

  private drawHalos() {
    // Don't needlessly create a group if there are no halos to draw.
    if (!this.selection.isEmpty) {
      this.#visual.withGroup({ desc: 'Selection Halos' }, () => {
        this.drawGroupHalos();
        this.drawEdgeHalos();
        this.drawNodeHalos();
      });
    }
  }

  private drawGroupHalos() {
    for (const node of this.selection.nodes) {
      if (this.#doc.isGroup(node)) {
        this.drawGroupHalo(node);
      }
    }
  }

  private drawGroups() {
    const groupNodes = topologicalSort(this.#doc.graph.forest).filter(node =>
      this.#doc.isGroup(node)
    );

    if (groupNodes.length > 0) {
      this.#visual.withGroup({ desc: 'Groups' }, () => {
        for (const node of groupNodes) {
          const title = this.#doc.nodeTitle(node).get(this.locale);
          this.#visual.withGroup({ id: node, title }, () => {
            this.drawGroupFrame(node, this.isHovered(node));
            this.drawNodeTitle(node);
          });
        }
      });
    }
  }

  private drawNodeHalos() {
    const active = this.selection.nodes.union(this.hovered.nodes);
    for (const node of active) {
      if (!this.#doc.graph.hasNode(node)) {
        console.warn(
          `drawNodeHalos: Node not found in graph: ${node}, isSelected: ${this.selection.hasNode(node).toString()}, isHovered: ${this.hovered.hasNode(node).toString()}`
        );
        continue;
      }
      if (!this.#doc.isGroup(node)) {
        this.drawNodeHalo(node);
      }
    }
  }

  private drawUnits() {
    const units: NID[] = [];
    for (const node of this.#doc.graph.nodes) {
      if (!this.#doc.isGroup(node)) {
        units.push(node);
      }
    }

    if (units.length > 0) {
      this.#visual.withGroup({ desc: 'Units' }, () => {
        for (const unit of units) {
          const title = this.#doc.nodeTitle(unit).get(this.locale);
          this.#visual.withGroup({ id: unit, title }, () => {
            // this.drawNodeFrame(node, this.hovered.hasNode(node));
            // this.drawNodeTitle(node);

            this.drawNode(unit);
          });
        }
      });
    }
  }

  private drawEdgeHalos() {
    for (const edge of this.selection.edges) {
      this.drawEdgeHalo(edge);
    }
  }

  private drawEdges() {
    this.#visual.withGroup({ desc: 'Edges' }, () => {
      for (const edge of this.#doc.graph.edges) {
        this.drawEdge(edge, this.hovered.hasEdge(edge));
      }
    });
  }

  private drawEndpointHighlights() {
    this.drawEndpointHighlight(this.highlightedEndpoint);
  }

  private drawEndpointHighlight(edgeEndpoint?: EdgeEndpoint) {
    if (edgeEndpoint === undefined) {
      return;
    }
    const { edge, direction } = edgeEndpoint;
    const layout = this.#doc.edgeLayout(edge);
    let p: Point;
    switch (direction) {
      case EdgeDirection.TAIL:
        p = layout.path.pointAt(0);
        break;
      case EdgeDirection.HEAD:
        p = layout.path.pointAt(1);
        break;
      default:
        throw new Error(`Unknown direction ${direction as string}`);
    }
    const opacity = layout.opacity * LayoutConstants.ENDPOINT_HIGHLIGHT_OPACITY;
    const fillColor = this.getColor(ColorName.edgeEndpointHighlight).withOpacity(opacity);
    drawMarker(this.#visual, p, {
      radius: LayoutConstants.ENDPOINT_HIGHTLIGHT_RADIUS,
      fillColor
    });
  }

  private drawEdge(edge: EID, isHovered: boolean) {
    const layout = this.#doc.edgeLayout(edge);
    const opacity = layout.opacity;
    let edgeColor = this.getColor(ColorName.edge);
    if (this.copied.hasEdge(edge)) {
      edgeColor = edgeColor.lerp(0.8, this.getColor(ColorName.copyHighlight));
    }
    edgeColor = edgeColor.withOpacity(opacity);
    const expand = isHovered ? LayoutConstants.HOVERED_EDGE_EXPAND : 0;
    const [source, target] = this.#doc.graph.endpoints(edge);
    this.#visual.withGroup({ id: edge, attrs: { source, target } }, () => {
      drawArrowPath(
        this.#visual,
        layout.path,
        edgeColor,
        LayoutConstants.EDGE_WIDTH,
        expand,
        { headType: ArrowType.TRIANGLE }
      );
    });
  }

  private drawEdgeHalo(edge: EID) {
    const layout = this.#doc.edgeLayout(edge);
    const opacity = layout.opacity * this.selection.edgeOpacity(edge);
    if (opacity > 0) {
      const expand =
        LayoutConstants.SELECTION_HALO_WIDTH +
        (this.hovered.hasEdge(edge) ? LayoutConstants.HOVERED_EDGE_EXPAND : 0);
      drawArrowPath(
        this.#visual,
        layout.path,
        this.getColor(ColorName.selectionHalo).withOpacity(opacity),
        LayoutConstants.EDGE_WIDTH,
        expand,
        { headType: ArrowType.TRIANGLE }
      );
    }
  }

  private drawGroupHalo(node: NID) {
    const layout = this.#doc.nodeLayout(node);
    const bounds = layout.bounds;

    const opacity = layout.opacity;
    const haloOpacity = opacity * this.selection.nodeOpacity(node);
    if (haloOpacity > 0) {
      const strokeWidth = 0;
      this.drawRectHalo(
        bounds,
        ColorName.selectionHalo,
        haloOpacity,
        LayoutConstants.GROUP_CORNER_RADIUS,
        strokeWidth
      );
    }
  }

  private drawGroupFrame(node: NID, isHovered: boolean) {
    const layout = this.#doc.nodeLayout(node) as GroupLayout;
    const bounds = layout.bounds;

    const opacity = layout.opacity;
    const groupColor = layout.color.realize(this.#visual.colorMode);

    this.#visual.drawRect(bounds, {
      fill: groupColor.withOpacity(0.2 * opacity)
    });

    if (isHovered) {
      const width = LayoutConstants.HOVERED_GROUP_STROKE_WIDTH;
      const b = bounds.expandingBy(-width / 2);
      this.#visual.drawRect(b, {
        stroke: { color: groupColor.withOpacity(0.5 * opacity), width }
      });
    }

    if (this.copied.hasNode(node)) {
      // For groups, the copy overlay should exactly cover the outer boundary.
      // Since the hover stroke is drawn inward, the bounds already represent the outer edge.
      this.#visual.drawRect(bounds, {
        fill: this.getColor(ColorName.copyHighlight).withOpacity(0.5 * opacity)
      });
    }
  }

  private nodeStrokeWidth(isHovered: boolean): number {
    return isHovered
      ? LayoutConstants.HOVERED_NODE_STROKE_WIDTH
      : LayoutConstants.NODE_STROKE_WIDTH;
  }

  private drawNodeHalo(node: NID) {
    const layout = this.#doc.nodeLayout(node);
    const opacity = layout.opacity * this.selection.nodeOpacity(node);
    if (opacity > 0) {
      const bounds = layout.bounds;
      const strokeWidth = this.nodeStrokeWidth(this.hovered.hasNode(node));
      this.drawRectHalo(
        bounds,
        ColorName.selectionHalo,
        opacity,
        LayoutConstants.NODE_CORNER_RADIUS,
        strokeWidth
      );
    }
  }

  private drawRectHalo(
    bounds: Rect,
    color: ColorName,
    opacity: number,
    cornerRadius: number,
    rectStrokeWidth: number
  ) {
    const expand = (rectStrokeWidth + LayoutConstants.SELECTION_HALO_WIDTH) / 2;
    this.#visual.drawRect(bounds.expandingBy(expand), {
      cornerRadius: cornerRadius + expand,
      stroke: {
        color: this.getColor(color).withOpacity(opacity),
        width: LayoutConstants.SELECTION_HALO_WIDTH
      }
    });
  }

  private drawNodeTitle(node: NID) {
    const spec = this.#doc.graph.nodeData(node);
    const layout = this.#doc.nodeLayout(node);
    let position = this.titlePosition(node, layout);
    const endLayout = spec.endLayout;
    if (endLayout !== undefined) {
      const t = spec.endOpacity ?? 0;
      const endPosition = this.titlePosition(node, endLayout);
      position = position.lerp(t, endPosition);
      this.drawNodeLayout(node, layout, position, 1 - t);
      this.drawNodeLayout(node, endLayout, position, t);
    } else {
      this.drawNodeLayout(node, layout, position, 1);
    }
  }

  private drawNode(node: NID) {
    const layout = this.#doc.nodeLayout(node);
    const snorfLayout = expectDefined(layout.snorfLayout);
    const updatedLayout = produce<SnorfLayout>(snorfLayout, draft => {
      const elem = expectDefined(draft.layoutElement(ENTITY_TOP));
      elem.hovered = this.isHovered(node);
    });
    const visual = this.#visual;
    visual.save();
    visual.translate(layout.bounds.topLeft);
    renderLayout(updatedLayout, visual, { mark: node });
    visual.restore();
  }

  private drawNodeLayout(
    node: NID,
    layout: NodeLayout,
    position: Point,
    transitionOpacity: number
  ) {
    if (!(layout instanceof UnitLayout) && !(layout instanceof GroupLayout)) {
      throw new Error('Unexpected node layout type');
    }

    const bounds = layout.bounds;
    const opacity = layout.opacity * transitionOpacity;
    const measuredTitle = expectDefined(layout.measuredTitle);
    const paragraph = this.#visual.cache.paragraphCache.get(
      measuredTitle.paragraphId,
      this.#visual.colorMode
    );

    if (paragraph === undefined) {
      console.warn('Paragraph not found in cache');
      return;
    }
    this.#visual.drawParagraph(paragraph, bounds, position, opacity);
  }

  private titlePosition(node: NID, layout: NodeLayout): Point {
    if (!(layout instanceof UnitLayout) && !(layout instanceof GroupLayout)) {
      throw new Error('Unexpected node layout type');
    }

    const bounds = layout.bounds;
    const measuredTitle = expectDefined(layout.measuredTitle);
    const paragraph = this.#visual.cache.paragraphCache.get(
      measuredTitle.paragraphId,
      this.#visual.colorMode
    );

    if (paragraph === undefined) {
      console.warn('Paragraph not found in cache');
      return Point.ZERO;
    }

    // KLUDGE ALERT:
    // For some reason we need to call `layout` again here
    // or some RTL text will be drawn with an incorrect offset.
    paragraph.layout(measuredTitle.size.width);

    const dir = textDirectionForParagraph(paragraph);
    if (this.#doc.isGroup(node)) {
      const x =
        dir === TextDirection.RTL
          ? bounds.right - measuredTitle.size.width - LayoutConstants.GROUP_TITLE_PADDING
          : LayoutConstants.GROUP_TITLE_PADDING + bounds.left;
      const y = LayoutConstants.GROUP_TITLE_PADDING + bounds.top;
      return point(x, y);
    } else {
      const deltaX =
        bounds.width - measuredTitle.size.width - LayoutConstants.UNIT_TITLE_PADDING * 2;
      const halfDeltaX = deltaX / 2;
      const x =
        dir === TextDirection.RTL
          ? bounds.right -
            halfDeltaX -
            measuredTitle.size.width -
            LayoutConstants.UNIT_TITLE_PADDING
          : halfDeltaX + LayoutConstants.UNIT_TITLE_PADDING + bounds.left;
      const deltaY =
        bounds.height -
        measuredTitle.size.height -
        LayoutConstants.UNIT_TITLE_PADDING * 2;
      const halfDeltaY = deltaY / 2;
      const y = halfDeltaY + LayoutConstants.UNIT_TITLE_PADDING + bounds.top;
      return point(x, y);
    }
  }

  // Draw a straight line between the centers of the source and target nodes.
  private drawUnroutedEdge(edge: EID) {
    const doc = this.#doc;
    const source = doc.graph.source(edge);
    const target = doc.graph.target(edge);
    const sourcePos = doc.nodeBounds(source).center;
    const targetPos = doc.nodeBounds(target).center;
    const line = lineSegment(sourcePos, targetPos);
    drawLineSegment(this.#visual, line, this.getColor(ColorName.edge), 2);
  }
}

export function drawGrid(
  visual: Visual,
  bounds: Rect,
  scale: number,
  emphasizeAxes: boolean
) {
  const step = 10;

  function drawLayer(
    desc: string,
    step: number,
    excludeNextLevel: boolean,
    strokeWidth: number,
    color: Color
  ) {
    const lineSegments: LineSegment[] = [];
    const startX = (Math.ceil(bounds.left / step) - 1) * step;
    const startY = (Math.ceil(bounds.top / step) - 1) * step;
    const endX = Math.floor(bounds.right / step) * step;
    const endY = Math.floor(bounds.bottom / step) * step;
    const step10 = step * 10;
    for (let x = startX; x <= endX; x += step) {
      if (x < bounds.left || x > bounds.right) {
        continue;
      }
      if (emphasizeAxes && x === 0) {
        continue;
      }
      if (!excludeNextLevel || x % step10 !== 0) {
        lineSegments.push(lineSegment(x, bounds.top, x, bounds.bottom));
      }
    }
    for (let y = startY; y <= endY; y += step) {
      if (y < bounds.top || y > bounds.bottom) {
        continue;
      }
      if (emphasizeAxes && y === 0) {
        continue;
      }
      if (!excludeNextLevel || y % step10 !== 0) {
        lineSegments.push(lineSegment(bounds.left, y, bounds.right, y));
      }
    }

    // Don't needlessly create a group if there are no line segments to draw
    if (lineSegments.length > 0) {
      visual.withGroup({ desc, strokeWidth, color }, paint => {
        for (const lineSegment of lineSegments) {
          visual.drawLine(lineSegment, paint);
        }
      });
    }
  }

  function drawAxes(strokeWidth: number, color: Color) {
    visual.withGroup({ strokeWidth, color, desc: 'Origin Axes Gridlines' }, paint => {
      visual.drawLine(lineSegment(0, bounds.top, 0, bounds.bottom), paint);
      visual.drawLine(lineSegment(bounds.left, 0, bounds.right, 0), paint);
    });
  }

  visual.withGroup({ desc: 'Grid' }, () => {
    const mode = visual.colorMode;
    const opacity10 = clamp(lerpMap(scale, [0.5, 2.0], [0.0, 0.4]));
    if (opacity10 > 0) {
      drawLayer(
        '10-Unit Gridlines',
        step,
        true,
        0.25,
        getColor(ColorName.canvasGrid, mode).withOpacity(opacity10)
      );
    }

    const opacity100 = clamp(lerpMap(scale, [0.1, 2.0], [0.3, 0.4]));
    if (opacity100 > 0) {
      drawLayer(
        '100-Unit Gridlines',
        step * 10,
        true,
        0.5,
        getColor(ColorName.canvasGrid, mode).withOpacity(opacity100)
      );
    }

    const opacity1000 = clamp(lerpMap(scale, [0.1, 2.0], [0.8, 0.5]));
    if (opacity1000 > 0) {
      drawLayer(
        '1000-Unit Gridlines',
        step * 100,
        false,
        0.8,
        getColor(ColorName.canvasGrid, mode).withOpacity(opacity1000)
      );
    }

    if (emphasizeAxes) {
      const strokeWidth = 1;
      const primary = AdaptiveColor.GRAY.realize(mode);
      const color = getColor(ColorName.canvasGrid, mode).lerp(0.5, primary);
      drawAxes(strokeWidth, color);
    }
  });
}
